import { QueryFactory } from '@lmt/shared-types';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { ConfigureColumns } from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/configure/ConfigureColumns';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';

import styles from '../CustomDictionaryPage.module.scss';
import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { Button, ButtonColor } from '../../../common/ui-kit/buttons/Button';

import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { DictionaryData, dictionaryDataYupSchema } from './common';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

type ConfigureDictionaryProps = FormComponentProps & {
  dictionaryId: number;
};
export const ConfigureDictionary: FC<ConfigureDictionaryProps> = (props) => {
  const { formType, formId, tryCompleteForm, loadBlocking, dictionaryId } =
    props;

  const i18next = useScopedTranslation('custom_dictionary.configure');
  const queryClient = useQueryClient();
  const claims = useClaims();
  const modals = useModal();

  const dictionariesQuery = QueryFactory.DataBlockDictionaryQuery.useGetQuery();
  const currentDictionary = useMemo(() => {
    return dictionariesQuery.data?.find((x) => x.id === dictionaryId);
  }, [dictionariesQuery.data]);

  const form = useAdvancedForm<DictionaryData>(
    useCallback(
      async (data) => {
        try {
          await tryCompleteForm(
            formType,
            formId,
            async () => {
              await QueryFactory.DataBlockDictionaryClient.patch(dictionaryId, {
                columns:
                  claims.permissions.advancedDictionarySettings &&
                  !currentDictionary?.isExternal
                    ? data.columns
                    : undefined,
                manualOrdering: data.manualOrdering,
              });
              await queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.DataBlockFieldQuery.getByIdQueryKey(
                    dictionaryId,
                  ),
              });
              await queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.DataBlockFieldQuery.getQueryKey(dictionaryId),
              });
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.DataBlockDictionaryQuery.getQueryKey(),
              });
            },
            i18next.t('saving'),
          );
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      },
      [formType, formId, tryCompleteForm],
    ),
    currentDictionary,
    {
      resolver: yupResolver(dictionaryDataYupSchema),
      shouldFocusError: true,
      criteriaMode: 'all',
    },
  );

  const deleteDictionary = useCallback(async () => {
    const isConfirmed = await modals.showDeletionConfirmation({
      title: i18next.t('confirm_delete_title'),
      text: i18next.t('confirm_delete_text'),
      okButtonText: i18next.t('confirm_delete_button'),
    });
    if (isConfirmed) {
      loadBlocking(formId, i18next.t('loading_delete'), async () => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            await QueryFactory.DataBlockDictionaryClient.remove(dictionaryId);
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DataBlockDictionaryQuery.getQueryKey(),
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }).catch(console.error);
    }
  }, [formType, formId, tryCompleteForm, dictionaryId]);

  return (
    <form
      onSubmit={form.handleSubmitDefault}
      className={globalFormStyles.autoSizedContainer}
      data-test-id="configureDictionaryPopup"
    >
      <div className={globalFormStyles.dataContainer}>
        {(claims.permissions.dictionariesManagement ||
          claims.permissions.advancedDictionarySettings) && (
          <CheckBox
            title={i18next.t('manual_ordering')}
            {...form.register('manualOrdering')}
            className={styles.manualOrderingCheckbox}
          />
        )}
        {claims.permissions.advancedDictionarySettings &&
          !currentDictionary?.isExternal && (
            <>
              <div className={styles.columnsTitle}>
                {i18next.t('columns_field_title')}
              </div>
              <ConfigureColumns form={form} />
            </>
          )}
      </div>
      <div className={globalFormStyles.submitContainer}>
        {claims.permissions.advancedDictionarySettings &&
          !currentDictionary?.isExternal && (
            <Button
              className={globalFormStyles.submit}
              color={ButtonColor.Danger}
              title={i18next.t('delete_button')}
              onClick={deleteDictionary}
            />
          )}
        <SubmitButton
          submitting={form.formState.isSubmitting}
          className={globalFormStyles.submit}
          title={i18next.t('save_button_title')}
        />
      </div>
    </form>
  );
};
